import React, { useState, useEffect } from 'react'
import { Card, Col, DatePicker, Form, Input, Row, Select, theme, Typography, Space, Radio, Alert, Divider } from 'antd'
import EntitySelect from './EntitySelect'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faUser,
  faUserMd,
  faBuilding,
  faCalendarAlt,
  faFileInvoiceDollar,
  faKey,
  faHardHat,
  faMedkit,
  faShieldAlt,
  faCar,
  faIdCard,
  faBriefcase,
  faClipboardList,
  faExclamationTriangle,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons'

/**
 * Component for handling invoice patient and practitioner details form
 * Now enhanced to support compensation fund claims (COID, FEM, SAPS, RMA)
 */
const InvoiceDetails = ({ isNew, saveForLaterEnabled, onEntitySelect, invoiceData, form }) => {
  const { token } = theme.useToken()
  const { Title } = Typography
  const [claimType, setClaimType] = useState('medical_aid')
  const [showWCAFields, setShowWCAFields] = useState(false)
  const [showLegacyCompensationFields, setShowLegacyCompensationFields] = useState(false)

  // Watch for invoice type changes to show/hide WCA and legacy compensation fields
  useEffect(() => {
    if (invoiceData?.claimType) {
      setClaimType(invoiceData.claimType)
      setShowWCAFields(invoiceData.claimType === 'WCA' || invoiceData.invoiceType === 'WCA')
      setShowLegacyCompensationFields(['COID', 'FEM', 'SAPS', 'RMA'].includes(invoiceData.claimType))
    }
  }, [invoiceData])

  // Watch for form field changes to show/hide WCA fields
  useEffect(() => {
    if (form) {
      const checkFormValues = () => {
        const currentInvoiceType = form.getFieldValue('invoiceType')
        const currentClaimType = form.getFieldValue('claimType')

        console.log(
          '🔍 [INVOICE-DETAILS] Form values changed - Invoice Type:',
          currentInvoiceType,
          'Claim Type:',
          currentClaimType
        )

        if (currentInvoiceType === 'WCA' || currentClaimType === 'WCA') {
          setShowWCAFields(true)
          setShowLegacyCompensationFields(false)
          setClaimType('WCA')
          console.log('🔍 [INVOICE-DETAILS] Showing WCA fields')
        } else if (['COID', 'FEM', 'SAPS', 'RMA'].includes(currentClaimType)) {
          setShowWCAFields(false)
          setShowLegacyCompensationFields(true)
          setClaimType(currentClaimType)
          console.log('🔍 [INVOICE-DETAILS] Showing legacy compensation fields')
        } else {
          setShowWCAFields(false)
          setShowLegacyCompensationFields(false)
          setClaimType(currentClaimType || 'medical_aid')
          console.log('🔍 [INVOICE-DETAILS] Hiding all special fields')
        }
      }

      // Check immediately
      checkFormValues()

      // Set up form field listener
      const unsubscribe = form.getFieldsValue && form.onFieldsChange && form.onFieldsChange(checkFormValues)

      return () => {
        if (unsubscribe && typeof unsubscribe === 'function') {
          unsubscribe()
        }
      }
    }
  }, [form, invoiceData])

  // Handle invoice type changes
  const handleInvoiceTypeChange = (value) => {
    console.log('🔍 [INVOICE-TYPE] Changed to:', value)
    console.log('🔍 [INVOICE-TYPE] Props - isNew:', isNew, 'saveForLaterEnabled:', saveForLaterEnabled)

    if (form) {
      // Use setTimeout to ensure the form update happens after the current render cycle
      setTimeout(() => {
        console.log('🔍 [INVOICE-TYPE] Updating form fields...')

        if (value === 'medical') {
          // Medical aid - show auth fields
          form.setFieldsValue({
            invoiceType: value,
            isMedicalAid: true,
            claimType: 'medical_aid'
          })
          setShowWCAFields(false)
          setShowLegacyCompensationFields(false)
          console.log('🔍 [INVOICE-TYPE] Set to medical aid')
        } else if (value === 'private') {
          // Private pay - clear medical aid fields
          form.setFieldsValue({
            invoiceType: value,
            isMedicalAid: false,
            authNo: null,
            userSchemeCode: null,
            claimType: 'private'
          })
          setShowWCAFields(false)
          setShowLegacyCompensationFields(false)
          console.log('🔍 [INVOICE-TYPE] Set to private pay')
        } else if (value === 'WCA') {
          // WCA (Workman's Compensation Act) - show WCA fields
          form.setFieldsValue({
            invoiceType: value,
            isMedicalAid: false,
            authNo: null,
            userSchemeCode: null,
            claimType: 'WCA'
          })
          setClaimType('WCA')
          setShowWCAFields(true)
          setShowLegacyCompensationFields(false)
          console.log('🔍 [INVOICE-TYPE] Set to WCA')
        } else if (value === 'insurance') {
          // Insurance - could be legacy compensation, show legacy compensation fields
          form.setFieldsValue({
            invoiceType: value,
            isMedicalAid: false,
            authNo: null,
            userSchemeCode: null
          })
          // Default to COID if no specific compensation type set
          if (!['COID', 'FEM', 'SAPS', 'RMA'].includes(form.getFieldValue('claimType'))) {
            form.setFieldsValue({ claimType: 'COID' })
            setClaimType('COID')
          }
          setShowWCAFields(false)
          setShowLegacyCompensationFields(true)
          console.log('🔍 [INVOICE-TYPE] Set to insurance/legacy compensation')
        }

        // Log the updated form values
        console.log('🔍 [INVOICE-TYPE] Updated form values:', form.getFieldsValue())
      }, 0)
    }
  }

  const compensationFundTypes = [
    {
      value: 'COID',
      label: 'COID - Occupational Injuries',
      icon: faHardHat,
      color: '#1890ff',
      description: 'Compensation for Occupational Injuries and Diseases'
    },
    {
      value: 'FEM',
      label: 'FEM - Employers Mutual',
      icon: faMedkit,
      color: '#52c41a',
      description: 'Federated Employers Mutual insurance claims'
    },
    {
      value: 'SAPS',
      label: 'SAPS - Police Service',
      icon: faShieldAlt,
      color: '#722ed1',
      description: 'South African Police Service injury claims'
    },
    {
      value: 'RMA',
      label: 'RMA - Road Accident Fund',
      icon: faCar,
      color: '#fa8c16',
      description: 'Road Accident Fund claims'
    }
  ]

  const renderEntitySelect = (type, refType = '') => {
    let initialValue = ''

    if (!isNew && saveForLaterEnabled) {
      switch (type) {
        case 'Patient':
          initialValue = invoiceData?.patientRecord?.firstName + ' ' + invoiceData?.patientRecord?.lastName
          break
        case 'Practitioner':
          if (refType === 'ref') {
            if (invoiceData?.refMedProfRecord) {
              initialValue = invoiceData?.refMedProfRecord?.firstName + ' ' + invoiceData?.refMedProfRecord?.lastName
            }
          } else if (refType === 'ass') {
            if (invoiceData?.assMedProfRecord) {
              initialValue = invoiceData?.assMedProfRecord?.firstName + ' ' + invoiceData?.assMedProfRecord?.lastName
            }
          } else {
            initialValue = invoiceData?.medicalProfRecord?.firstName + ' ' + invoiceData?.medicalProfRecord?.lastName
          }
          break
        case 'Clinic':
          initialValue = invoiceData?.clinicRecord?.name
          break
      }
    }

    return <EntitySelect type={type} onSelect={onEntitySelect} initialValue={initialValue} />
  }

  // Form item style to be consistent
  const formItemStyle = {
    marginBottom: '16px'
  }

  // Input prefix icon styling
  const iconStyle = {
    color: token.colorPrimary,
    marginRight: '8px'
  }

  // Debug the disabled state
  const isInvoiceTypeDisabled = !isNew && !saveForLaterEnabled
  console.log('🔍 [INVOICE-TYPE] Disabled state:', {
    isNew,
    saveForLaterEnabled,
    isDisabled: isInvoiceTypeDisabled
  })

  return (
    <>
      {/* Patient & Practitioner Details Card */}
      <Card
        bordered
        style={{
          marginBottom: '16px',
          borderRadius: '8px',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)'
        }}
      >
        <div style={{ marginBottom: '16px' }}>
          <Title level={5} style={{ margin: 0, color: token.colorTextSecondary }}>
            <FontAwesomeIcon icon={faUser} style={{ marginRight: '8px', color: token.colorPrimary }} />
            Patient & Practitioner Details
          </Title>
        </div>

        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Form.Item
              name='userRef'
              label='Patient'
              rules={[{ required: true, message: 'Patient is required' }]}
              style={formItemStyle}
            >
              {isNew || (!isNew && saveForLaterEnabled) ? (
                renderEntitySelect('Patient')
              ) : (
                <Input readOnly prefix={<FontAwesomeIcon icon={faUser} style={iconStyle} />} />
              )}
            </Form.Item>

            <Row gutter={[16, 0]}>
              <Col span={12}>
                <Form.Item
                  name='medicalProfRef'
                  label='Practitioner'
                  rules={[{ required: true, message: 'Practitioner is required' }]}
                  style={formItemStyle}
                >
                  {isNew || (!isNew && saveForLaterEnabled) ? (
                    renderEntitySelect('Practitioner')
                  ) : (
                    <Input readOnly prefix={<FontAwesomeIcon icon={faUserMd} style={iconStyle} />} />
                  )}
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name='bookingDate'
                  label='Booking Date'
                  rules={[{ required: true, message: 'Booking Date is required' }]}
                  style={formItemStyle}
                >
                  {isNew || (!isNew && saveForLaterEnabled) ? (
                    <DatePicker
                      style={{ width: '100%' }}
                      format='YYYY-MM-DD'
                      suffixIcon={<FontAwesomeIcon icon={faCalendarAlt} style={{ color: token.colorPrimary }} />}
                      placeholder='Select booking date'
                    />
                  ) : (
                    <Input readOnly prefix={<FontAwesomeIcon icon={faCalendarAlt} style={iconStyle} />} />
                  )}
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[16, 0]}>
              <Col span={12}>
                <Form.Item
                  name='clinicRef'
                  label='Venue'
                  rules={[{ required: true, message: 'Venue is required' }]}
                  style={formItemStyle}
                >
                  {isNew || (!isNew && saveForLaterEnabled) ? (
                    renderEntitySelect('Clinic')
                  ) : (
                    <Input readOnly prefix={<FontAwesomeIcon icon={faBuilding} style={iconStyle} />} />
                  )}
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name='place' label='Place' style={formItemStyle}>
                  <Select
                    disabled={!isNew && !saveForLaterEnabled}
                    defaultValue='outHospital'
                    options={[
                      { label: 'Out Hospital', value: 'outHospital' },
                      { label: 'In Hospital', value: 'inHospital' }
                    ]}
                    placeholder='Select place'
                  />
                </Form.Item>
              </Col>
            </Row>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              name='invoiceType'
              label='Invoice Type'
              rules={[{ required: true, message: 'Invoice Type is required' }]}
              style={formItemStyle}
            >
              <Select
                disabled={false}
                onChange={handleInvoiceTypeChange}
                options={[
                  {
                    label: 'Medical Aid',
                    value: 'medical',
                    disabled: false
                  },
                  {
                    label: 'Private Pay',
                    value: 'private',
                    disabled: false
                  },
                  {
                    label: "WCA (Workman's Compensation)",
                    value: 'WCA',
                    disabled: false
                  },
                  {
                    label: 'Legacy Insurance / Compensation Fund',
                    value: 'insurance',
                    disabled: false
                  }
                ]}
                suffixIcon={<FontAwesomeIcon icon={faFileInvoiceDollar} style={{ color: token.colorPrimary }} />}
                placeholder='Select invoice type'
              />
            </Form.Item>

            {/* Legacy Compensation Fund Type Selection - Only show when Insurance is selected */}
            {showLegacyCompensationFields && (
              <Form.Item
                name='claimType'
                label='Legacy Compensation Fund Type'
                rules={[{ required: true, message: 'Please select compensation fund type' }]}
                style={formItemStyle}
              >
                <Select
                  disabled={!isNew && !saveForLaterEnabled}
                  onChange={(value) => setClaimType(value)}
                  placeholder='Select compensation fund'
                  suffixIcon={<FontAwesomeIcon icon={faShieldAlt} style={{ color: token.colorPrimary }} />}
                >
                  {compensationFundTypes.map((fund) => (
                    <Select.Option key={fund.value} value={fund.value}>
                      <Space>
                        <FontAwesomeIcon icon={fund.icon} style={{ color: fund.color, fontSize: '14px' }} />
                        <span>{fund.label}</span>
                      </Space>
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            )}

            {/* Medical Aid Specific Fields */}
            {!showWCAFields && !showLegacyCompensationFields && (
              <Form.Item name='authNo' label='Authorization Number' style={formItemStyle}>
                <Input
                  disabled={!isNew && !saveForLaterEnabled}
                  prefix={<FontAwesomeIcon icon={faKey} style={iconStyle} />}
                  placeholder='Enter authorization number'
                />
              </Form.Item>
            )}

            <Row gutter={[16, 0]}>
              <Col span={12}>
                <Form.Item name='refMedProfRef' label='Referring Doctor' style={formItemStyle}>
                  {isNew || (!isNew && saveForLaterEnabled) ? (
                    renderEntitySelect('Practitioner', 'ref')
                  ) : (
                    <Input readOnly prefix={<FontAwesomeIcon icon={faUserMd} style={iconStyle} />} />
                  )}
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name='assMedProfRef' label='Assisting Doctor' style={formItemStyle}>
                  {isNew || (!isNew && saveForLaterEnabled) ? (
                    renderEntitySelect('Practitioner', 'ass')
                  ) : (
                    <Input readOnly prefix={<FontAwesomeIcon icon={faUserMd} style={iconStyle} />} />
                  )}
                </Form.Item>
              </Col>
            </Row>
          </Col>
        </Row>
      </Card>

      {/* WCA Specific Details Card */}
      {showWCAFields && (
        <Card
          bordered
          style={{
            marginBottom: '16px',
            borderRadius: '8px',
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)',
            borderLeft: `4px solid ${token.colorPrimary}`
          }}
        >
          <div style={{ marginBottom: '16px' }}>
            <Title level={5} style={{ margin: 0, color: token.colorTextSecondary }}>
              <FontAwesomeIcon
                icon={faShieldAlt}
                style={{
                  marginRight: '8px',
                  color: token.colorPrimary
                }}
              />
              WCA (Workman's Compensation) Claim Details
            </Title>
          </div>

          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <Form.Item
                name='wcaClaimNumber'
                label='WCA Claim Number'
                rules={[{ required: showWCAFields, message: 'WCA claim number is required' }]}
                style={formItemStyle}
              >
                <Input
                  prefix={<FontAwesomeIcon icon={faClipboardList} style={iconStyle} />}
                  placeholder='Enter WCA claim number'
                  disabled={!isNew && !saveForLaterEnabled}
                />
              </Form.Item>

              <Form.Item
                name='wcaInjuryDate'
                label='Date of Injury'
                rules={[{ required: showWCAFields, message: 'Date of injury is required' }]}
                style={formItemStyle}
              >
                <DatePicker
                  style={{ width: '100%' }}
                  format='YYYY-MM-DD'
                  suffixIcon={<FontAwesomeIcon icon={faCalendarAlt} style={{ color: token.colorPrimary }} />}
                  placeholder='Select injury date'
                  disabled={!isNew && !saveForLaterEnabled}
                />
              </Form.Item>

              <Form.Item
                name='wcaEmployer'
                label='Employer Name'
                rules={[{ required: showWCAFields, message: 'Employer name is required' }]}
                style={formItemStyle}
              >
                <Input
                  prefix={<FontAwesomeIcon icon={faBriefcase} style={iconStyle} />}
                  placeholder='Enter employer name'
                  disabled={!isNew && !saveForLaterEnabled}
                />
              </Form.Item>

              <Form.Item name='wcaEmployerRegNumber' label='Employer Registration Number' style={formItemStyle}>
                <Input placeholder='Enter employer registration number' disabled={!isNew && !saveForLaterEnabled} />
              </Form.Item>

              <Form.Item name='wcaOccupation' label='Patient Occupation' style={formItemStyle}>
                <Input placeholder='Enter patient occupation' disabled={!isNew && !saveForLaterEnabled} />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                name='wcaInjuryDescription'
                label='Injury/Condition Description'
                rules={[{ required: showWCAFields, message: 'Please describe the injury or condition' }]}
                style={formItemStyle}
              >
                <Input.TextArea
                  rows={4}
                  placeholder='Provide a detailed description of the injury or condition...'
                  disabled={!isNew && !saveForLaterEnabled}
                />
              </Form.Item>

              <Form.Item
                name='wcaTreatmentDescription'
                label='Treatment Provided'
                rules={[{ required: showWCAFields, message: 'Please describe the treatment' }]}
                style={formItemStyle}
              >
                <Input.TextArea
                  rows={4}
                  placeholder='Describe the treatment provided...'
                  disabled={!isNew && !saveForLaterEnabled}
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>
      )}

      {/* Legacy Compensation Fund Specific Details Card */}
      {showLegacyCompensationFields && (
        <Card
          bordered
          style={{
            marginBottom: '16px',
            borderRadius: '8px',
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)',
            borderLeft: `4px solid ${
              compensationFundTypes.find((f) => f.value === claimType)?.color || token.colorPrimary
            }`
          }}
        >
          <div style={{ marginBottom: '16px' }}>
            <Title level={5} style={{ margin: 0, color: token.colorTextSecondary }}>
              <FontAwesomeIcon
                icon={compensationFundTypes.find((f) => f.value === claimType)?.icon || faExclamationTriangle}
                style={{
                  marginRight: '8px',
                  color: compensationFundTypes.find((f) => f.value === claimType)?.color || token.colorPrimary
                }}
              />
              Legacy {claimType} Claim Details (Deprecated)
            </Title>
          </div>

          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <Form.Item
                name='iod_claimnumber'
                label='Claim Reference Number'
                rules={[{ required: showLegacyCompensationFields, message: 'Claim number is required' }]}
                style={formItemStyle}
              >
                <Input
                  prefix={<FontAwesomeIcon icon={faClipboardList} style={iconStyle} />}
                  placeholder={`Enter ${claimType} claim number`}
                  disabled={!isNew && !saveForLaterEnabled}
                />
              </Form.Item>

              <Form.Item
                name='iod_injury_date'
                label='Date of Injury/Incident'
                rules={[{ required: showLegacyCompensationFields, message: 'Date of injury is required' }]}
                style={formItemStyle}
              >
                <DatePicker
                  style={{ width: '100%' }}
                  format='YYYY-MM-DD'
                  suffixIcon={<FontAwesomeIcon icon={faCalendarAlt} style={{ color: token.colorPrimary }} />}
                  placeholder='Select injury date'
                  disabled={!isNew && !saveForLaterEnabled}
                />
              </Form.Item>

              {['COID', 'FEM'].includes(claimType) && (
                <>
                  <Form.Item
                    name='employerName'
                    label='Employer Name'
                    rules={[{ required: true, message: 'Employer name is required' }]}
                    style={formItemStyle}
                  >
                    <Input
                      prefix={<FontAwesomeIcon icon={faBriefcase} style={iconStyle} />}
                      placeholder='Enter employer name'
                      disabled={!isNew && !saveForLaterEnabled}
                    />
                  </Form.Item>

                  <Form.Item name='employerReference' label='Employer Policy/Reference Number' style={formItemStyle}>
                    <Input
                      placeholder='Employer reference or policy number'
                      disabled={!isNew && !saveForLaterEnabled}
                    />
                  </Form.Item>
                </>
              )}

              {claimType === 'RMA' && (
                <>
                  <Form.Item name='caseNumber' label='Police Case Number' style={formItemStyle}>
                    <Input placeholder='Enter police case number' disabled={!isNew && !saveForLaterEnabled} />
                  </Form.Item>

                  <Form.Item name='accidentLocation' label='Accident Location' style={formItemStyle}>
                    <Input placeholder='Enter accident location' disabled={!isNew && !saveForLaterEnabled} />
                  </Form.Item>
                </>
              )}
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                name='iod_injury_description'
                label='Injury/Condition Description'
                rules={[{ required: showLegacyCompensationFields, message: 'Please describe the injury or condition' }]}
                style={formItemStyle}
              >
                <Input.TextArea
                  rows={4}
                  placeholder='Provide a detailed description of the injury or condition...'
                  disabled={!isNew && !saveForLaterEnabled}
                />
              </Form.Item>

              <Form.Item
                name='treatmentType'
                label='Treatment Provided'
                rules={[{ required: showLegacyCompensationFields, message: 'Please describe the treatment' }]}
                style={formItemStyle}
              >
                <Input.TextArea
                  rows={3}
                  placeholder='Describe the treatment provided...'
                  disabled={!isNew && !saveForLaterEnabled}
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>
      )}
    </>
  )
}

export default InvoiceDetails
